import {useFeatureValue} from '@growthbook/growthbook-react';

/**
 * Mirrors Nyx-iOS's `useGlp1Modality` hook. Reads the active modality from
 * three sources in priority order:
 *
 *   1. `?modality=branded_v1|compounded_v1` URL param (highest)
 *   2. `osa_qa_modality` localStorage entry (set by the QA screen)
 *   3. `web_glp1_modality` GrowthBook flag (production)
 *
 * NOTE: the flag is `web_glp1_modality`, NOT the app's `glp1_modality` —
 * the web funnel has its own flag so a web experiment doesn't move the
 * Nyx app's modality (and vice versa).
 *
 * Defaults to `branded_v1` if no source resolves to a known value.
 */
export type Glp1Modality = 'branded_v1' | 'compounded_v1';

/** Web-specific GrowthBook flag — deliberately distinct from the app's
 *  `glp1_modality` so the two surfaces can be controlled independently. */
export const MODALITY_FLAG = 'web_glp1_modality';
export const QA_MODALITY_STORAGE_KEY = 'osa_qa_modality';
export const QA_MODALITY_PARAM = 'modality';

const DEFAULT_MODALITY: Glp1Modality = 'branded_v1';
const KNOWN_MODALITIES = new Set<Glp1Modality>(['branded_v1', 'compounded_v1']);

const isKnown = (v: string | null | undefined): v is Glp1Modality =>
  v !== null && v !== undefined && KNOWN_MODALITIES.has(v as Glp1Modality);

/** Read the QA override layer (URL > localStorage). Lives outside the hook
 *  so non-hook contexts can read it too (e.g., one-off scripts in QAScreen). */
export const readModalityOverride = (): Glp1Modality | null => {
  if (typeof window === 'undefined') return null;
  const fromUrl = new URLSearchParams(window.location.search).get(QA_MODALITY_PARAM);
  if (isKnown(fromUrl)) return fromUrl;
  try {
    const fromStorage = window.localStorage.getItem(QA_MODALITY_STORAGE_KEY);
    if (isKnown(fromStorage)) return fromStorage;
  } catch {
    // localStorage may throw under privacy modes; fall through.
  }
  return null;
};

export const useGlp1Modality = (): Glp1Modality => {
  const override = readModalityOverride();
  const fromFlag = useFeatureValue<string>(MODALITY_FLAG, DEFAULT_MODALITY);
  if (override) return override;
  return isKnown(fromFlag) ? fromFlag : DEFAULT_MODALITY;
};
